import React, {useEffect, useState} from 'react'
import {
    Distribution,
    DistributionType,
    FamilyMembers,
    Member,
    MemberType,
    Trust,
    Trustee
} from '../../../model/EstateFlowchartTypes'
import {MemberGroup} from 'src/ClientManagement/models/InvestorGroupType'
import {useParams} from 'react-router-dom'
import {RouteWithId} from 'src/routes/types'
import SuccessorTable from '../../SuccessorTable/SuccessorTable'
import MemberDropdownMultiselect from '../MemberDropdownMultiselect/MemberDropdownMultiselect'
import PowersOfAppointmentSection from '../../PowersOfAppointmentSection/PowersOfAppointmentSection';
import LoadingIndicator from "../../../../pages/LoadingIndicator";
import {evaluateDropdownMemberId, isCustomMember, parseFamilyMembers} from "../../../utils/DefUtils";
import InputField from "../InputField/InputField";
import RadioGroupField from "../RadioGroupField/RadioGroupField";
import SingleBeneficiaryField from "../SingleBeneficiaryField/SingleBeneficiaryField";
import TextAreaField from "../TextAreaField/TextAreaField";
import {TRUST_FORM_CONSTANTS as Labels} from "../../../constants/LabelConstants";
import {familyMembersSelector} from "../../../slices/EstateFlowChartSlice";
import {getDistributionTypeAsString} from "../../../utils/TestUtils";
import FormPageSection from "../../FormPageSection/FormPageSection";
import {saveSelector} from "../../../slices/NavigationSlice";
import {getSectionId} from "../../FormNav/FormNavUtils";
import {TagsTooltipContent} from "../../TagsTooltipContent/TagsTooltipContent";
import {PowerOfAppointment} from "../../PowersOfAppointmentSection/PowerOfAppointment";
import {useAppSelector} from "../../../../store/hooks";

interface TrustFormProps {
    pageIndex: number;
    sectionIndex: number;
    trustInfo: Trust;
    updateDistribution: (trust: Distribution, index: number) => void;
    deleteDistribution: (index: number) => void;
}

/**
 * TrustForm component is responsible for rendering a form to manage the details of a trust.
 * It allows users to input and update information about the trust, including trustees, beneficiaries, funding, distributions, and termination details.
 *
 * @component
 * @param {TrustFormProps} props - The properties passed to the component.
 * @param {number} - The index of the form page
 * @param {number} - The index of the form page section
 * @param {Trust} props.trustInfo - Information about the trust.
 * @param {Function} props.deleteDistribution - Function to delete distribution.
 * @param {Function} props.updateDistribution - Function to add or update distribution.
 *
 * @returns {JSX.Element} The rendered TrustForm component.
 *
 * @example
 * <TrustForm
 *   trustInfo={trustInfo}
 *   addOrUpdateDistribution={addOrUpdateDistribution}
 *   deleteDistribution={deleteDistribution}
 *   index={index}
 * />
 */
function TrustForm({
                       pageIndex,
                       sectionIndex,
                       trustInfo,
                       deleteDistribution,
                       updateDistribution,
                   }: Readonly<TrustFormProps>): JSX.Element {

    const {id} = useParams<RouteWithId>();
    const saveHasBeenClicked = useAppSelector(saveSelector);
    const familyMembersRes = useAppSelector(familyMembersSelector);
    const [trust, setTrust] = useState<Trust>(trustInfo);

    const [trustees, setTrustees] = useState([{label: "Northern Trust", selected: false, value: ""}] as Member[])
    const [familyMembersResponse, setFamilyMembersResponse] = useState({} as FamilyMembers)
    const [beneficiaryOptions, setBeneficiaryOptions] = useState([] as Member[])
    const [familyMembers, setFamilyMembers] = useState({} as MemberGroup)


    const listHeight = (object: any) => {
        return object.length * 34 < 280 ? (object.length * 34) + 20 : 280
    }

    const onChangeDropdown = (selectedOptions: any, type: MemberType) => {
        const members: Trustee[] = [];
        for (const element of selectedOptions) {
            const member: Trustee = {
                trustId: trust.beneficiaryId ?? "",
                memberType: type,
                familyMember: !isCustomMember(element.value, familyMembersResponse),
                customName: isCustomMember(element.value, familyMembersResponse) ? element.label : "",
                memberId: evaluateDropdownMemberId(element, familyMembersResponse),
            };
            members.push(member);
        }

        switch (type) {
            case MemberType.Beneficiary:
                setTrust({...trust, beneficiaries: members} as Trust);
                break;
            case MemberType.Trustee:
                setTrust({...trust, trustees: members} as Trust);
                break;
            default:
        }
    }

    const sortedPowersOfAppointments = (): PowerOfAppointment[] => {
        if (trustInfo.powersOfAppointment) {
            return [...trustInfo.powersOfAppointment].sort((a, b) => a.poaOrder - b.poaOrder)
        }
        return [];
    }

    const updatePowersOfAppointments = (newPowersOfAppointments: PowerOfAppointment[]) => {
        setTrust({...trust, powersOfAppointment: newPowersOfAppointments});
    }

    useEffect(() => {
        const parsedFamilyMembersResponse: FamilyMembers = parseFamilyMembers(familyMembersRes);
        let item = {
            label: parsedFamilyMembersResponse.label,
            value: parsedFamilyMembersResponse.value,
            selected: false
        };
        parsedFamilyMembersResponse.familyMember.push(item);

        let beneficiaries = trust.beneficiaries;
        if (!trustInfo.beneficiaries.length &&
            trustInfo.distributionType === DistributionType.MARITAL &&
            familyMembersRes.partnerMember?.relationshipType === "SPOUSE") {

            beneficiaries = [{
                trustId: undefined,
                memberId: familyMembersRes.partnerMember?.id,
                memberType: MemberType.Beneficiary,
                familyMember: true,
                customName: "",
                memberOrder: undefined
            }];
        }

        let filteredBeneficiaryOptions = parsedFamilyMembersResponse.familyMember;
        if (trustInfo.distributionType === DistributionType.MARITAL) {
            const currentMemberIds = parsedFamilyMembersResponse.familyMember.map(m => m.value);
            const missingBeneficiaries = (beneficiaries || [])
                .filter(b => b.memberId && !currentMemberIds.includes(b.memberId))
                .map(b => ({
                    label: b.customName ?? b.memberId ?? '',
                    value: b.memberId ?? '',
                    selected: true
                } as Member));
            filteredBeneficiaryOptions = [...parsedFamilyMembersResponse.familyMember, ...missingBeneficiaries];
        }

        setBeneficiaryOptions(filteredBeneficiaryOptions);
        setTrust({...trustInfo, beneficiaries: beneficiaries});
        setFamilyMembers(familyMembersRes);
        setFamilyMembersResponse(parsedFamilyMembersResponse);
        setTrustees(filteredBeneficiaryOptions);
    }, [id, familyMembersRes]);

    useEffect(() => {
        updateDistribution(trust, sectionIndex);
    }, [trust]);

    if (beneficiaryOptions.length === 0) {
        return <LoadingIndicator/>
    }

    return (
        <FormPageSection id={getSectionId(pageIndex, sectionIndex)}
                         distributionStyle={"trust"}
                         headerText={getDistributionTypeAsString(trustInfo.distributionType)}
                         showRequiredLabel={true}
                         headerButtonIndex={sectionIndex}
                         headerIcon={"bank"}
                         showDeleteButton={true}
                         onClickButton={deleteDistribution}>

            <div className="distribution-form" aria-label="Trust Form" data-testid={'trust-form-' + sectionIndex}>
                <InputField label={Labels.TRUST_NAME_LABEL_TEXT}
                            name={trust.name}
                            required={true}
                            onChange={(name) => {
                                setTrust({...trust, name});
                                updateDistribution(trust, sectionIndex);
                            }}
                            liveUpdate={true}/>

                <RadioGroupField label={Labels.TAGS_LABEL_TEXT}
                                 id={"tags" + sectionIndex.toString()}
                                 values={["Exempt", "Non-Exempt", "Both", "Unknown/Inapplicable"]}
                                 selectedValue={trust.tags}
                                 required={true}
                                 tooltipContent={<TagsTooltipContent/>}
                                 onChange={(tags) => {
                                     setTrust({...trust, tags})
                                 }}/>

                <MemberDropdownMultiselect
                    label={Labels.TRUSTEES_LABEL_TEXT}
                    required={true}
                    options={familyMembers}
                    selectedOptions={trust.trustees}
                    includeNT={true}
                    useTags={true}
                    onChange={(options: any) => onChangeDropdown(options, MemberType.Trustee)}
                    onError={saveHasBeenClicked &&
                    (!trust.trustees || trust.trustees.length === 0) ?
                        "Trustees is required." :
                        undefined
                    }
                />

                <SuccessorTable formData={trust}
                                successorType={MemberType.Successor}
                                dropdownHeight={listHeight(trustees)}
                                updateData={setTrust}
                                showSubtitle={true}/>

                {
                    trust.distributionType === DistributionType.MARITAL ?
                        <SingleBeneficiaryField beneficiaries={trust.beneficiaries}
                                                beneficiaryOptions={beneficiaryOptions}
                                                onChange={(beneficiaries) => setTrust({
                                                    ...trust,
                                                    beneficiaries
                                                })}/> :
                        <MemberDropdownMultiselect
                            label="Beneficiaries"
                            required={false}
                            options={familyMembers}
                            selectedOptions={trust.beneficiaries}
                            includeNT={false}
                            useTags={true}
                            helperText='Add a new name, if it is not in the list.'
                            onChange={(options: any) => onChangeDropdown(options, MemberType.Beneficiary)}
                        />
                }

                <TextAreaField input={trust.funding}
                               label={Labels.FUNDING_LABEL_TEXT}
                               subText={Labels.FUNDING_SUB_TEXT}
                               onChange={(funding) => setTrust({...trust, funding})}/>

                <TextAreaField input={trust.distributions}
                               label={Labels.DISTRIBUTIONS_LABEL_TEXT}
                               onChange={(distributions) => {
                                   setTrust({...trust, distributions})
                               }}
                               subText={Labels.DISTRIBUTIONS_SUB_TEXT}
                               required={true}/>

                <TextAreaField input={trust.termination}
                               label={Labels.TERMINATION_LABEL_TEXT}
                               onChange={(termination) => setTrust({...trust, termination})}/>

                <PowersOfAppointmentSection powersOfAppointment={sortedPowersOfAppointments()}
                                            familyMembers={beneficiaryOptions}
                                            updateTrust={updatePowersOfAppointments}
                                            trustIndex={sectionIndex}/>
            </div>
        </FormPageSection>
    );
}

export default TrustForm

